import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Divider, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Card, CardContent, CardMedia, Typography, Button, TextField } from '@mui/material';
import { fetchSecrets, fetchPaymentStatus } from "../services/booking";
import { IMAGES } from '../constants';

const getCourtImage = (courtId) => {
  const courtImages = {
    1: IMAGES.FC1,
    2: IMAGES.FC2,
    3: IMAGES.BAD,
    4: IMAGES.LC1,
    5: IMAGES.LC2,
  };

  return courtImages[courtId] || IMAGES.img;
};

const BookingConfirmation = ({ bookingDetails }) => {

  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [secretData, setSecretData] = useState(null);
  
  const RETURN_URL = "https://phoenixplayzone.com/#/payment/status";
  const CANCEL_URL = "https://phoenixplayzone.com/#/payment/cancel";
  // const NOTIFY_URL = "https://13.211.123.4/api/payhere/notify";
  const NOTIFY_URL = "https://phoenix-booking-backend.vercel.app/api/payhere/notify";

  useEffect(() => {
    if (!window.payhere) {
      const script = document.createElement("script");
      script.src = "https://www.payhere.lk/lib/payhere.js";
      script.async = true;
      document.body.appendChild(script);
  
      script.onload = () => console.log("PayHere modal loaded");
  
      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const getSecretDetails = async () => {
      try {
        const data = await fetchSecrets(bookingDetails);
        if (data) {
          setSecretData(data);
          setIsError(false);
        } else {
          setDialogMessage("Gateway Unavailable. Please try again later.");
          setIsError(true);
        }
      } catch (error) {
        setDialogMessage("Gateway Error. Please try again later.");
        setIsError(true);
      }
    };

    getSecretDetails();
  }, [bookingDetails]);

  const handleConfirm = () => {
    const cid = bookingDetails.court.id;
    const price = bookingDetails.totalPrice;
    
    // Create a Date object for the start time
    const startTime = new Date(bookingDetails.date);
    const [start, end] = bookingDetails.timeRange.split(" - ");

    // Helper function to parse time like "4:00 PM" into hours and minutes
    const parseTime = (timeString) => {
      const timeParts = timeString.match(/(\d+):(\d+)\s([AP]M)/);
      let hours = parseInt(timeParts[1], 10);
      const minutes = parseInt(timeParts[2], 10);
      const period = timeParts[3];

      if (period === "PM" && hours !== 12) {
        hours += 12;
      } else if (period === "AM" && hours === 12) {
        hours = 0;
      }

      return { hours, minutes };
    };

    // Parse start time
    const startTimeParts = parseTime(start);
    startTime.setHours(startTimeParts.hours);
    startTime.setMinutes(startTimeParts.minutes);
    startTime.setSeconds(0);

    // Create a Date object for the end time
    const endTime = new Date(bookingDetails.date);
    const endTimeParts = parseTime(end);
    endTime.setHours(endTimeParts.hours);
    endTime.setMinutes(endTimeParts.minutes);
    endTime.setSeconds(0);

    // If endTime is before startTime, the end time is on the next day (last slot)
    if (endTime < startTime) {
      endTime.setDate(endTime.getDate() + 1);
    }

    // Calculate the duration in seconds
    const duration = (endTime - startTime) / 1000; // difference in seconds

    const customPayload = JSON.stringify({
      cid: cid,
      duration: duration, // Calculated duration in seconds
      price: price,
      startTime: startTime.toISOString(), // Start time in ISO format
      status: "PAID", // Static value
      title: bookingDetails.court.name,
      name: name,
      contact: Number(contactNumber),
      orderId: secretData.orderId,
    });

    // console.log(customPayload);
    
    const payment = {
      // sandbox: true,
      merchant_id: secretData.merchantId,
      return_url: RETURN_URL,
      cancel_url: CANCEL_URL, 
      notify_url: NOTIFY_URL,
      order_id: secretData.orderId,
      items: bookingDetails.court.name,
      amount: price.toFixed(2),
      currency: "LKR",
      first_name: name,
      last_name: name,
      email: "phoenixplayzone@gmail.com",
      phone: contactNumber,
      address: "No.1, Galle Road",
      city: "Galle",
      country: "Sri Lanka",
      hash: secretData.hash,
      custom_1: customPayload
    };

    if (window.payhere) {
      window.payhere.onCompleted = (orderId) => {
        // Call your backend to verify the payment
        fetchPaymentStatus(orderId)
          .then((data) => {
            if (data.status === "SUCCESS"){
              setDialogMessage("Booking was placed successfully");
              setIsError(false);
              navigate(`/payment/status?ref=${orderId}`)
            } else {
              navigate("/payment/failed");
            }
          })
          .catch(() => {
            // TODO
            navigate(`/payment/status?ref=${orderId}`)
          })
      };

      window.payhere.onDismissed = () => navigate("/payment/cancel");
      window.payhere.onError = () => navigate("/payment/failed");
      try {
        window.payhere.startPayment(payment);
      } catch (error) {
        console.error("Payment initiation error");
      }
    } else {
      console.error("PayHere script not loaded");
    }
  };

  // Check if both name and contact number are provided && secret are fetched
  const isConfirmEnabled = secretData && name.trim() !== "" && contactNumber.trim().length === 10;

  return (
    <div className="flex flex-col items-center">
      <Typography variant="body1" sx={{ mb: 2, color: "#f59e0b" }}>
        Please arrive on time before your reserved slot
      </Typography>
      <Card className="p-4 max-w-sm w-full" sx={{ backgroundColor: "#f9fafb", color: "#1f2937" }}>
        <CardMedia
          component="img"
          height="200"
          image={getCourtImage(bookingDetails.court.id)}
          alt="Court Image"
        />
        <CardContent sx={{ textAlign: "left", px:0 }}>
          <Typography variant="h6" sx={{ textAlign: "center", mb: 1, letterSpacing: 1, color: "#1f2937" }}>
            Booking Summary
          </Typography>
          <Box sx={{ maxWidth: 400, mx: "auto", px: 1.5, py: 0.5, boxShadow: 1, borderRadius: 2, bgcolor: "#f9fafb", color: "#1f2937" }}>
            {[
              { label: "Court", value: bookingDetails.court.name },
              { label: "Date", value: new Date(bookingDetails.date).toLocaleDateString() },
              { label: "Time", value: bookingDetails.timeRange },
              { label: "Total Price", value: `Rs. ${bookingDetails.totalPrice}` },
            ].map((item, index) => (
              <Box key={index}>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, alignItems: "center" }}>
                  <Typography variant="body1" sx={{ letterSpacing: 1, color: "#374151" }}>{item.label}</Typography>
                  <Typography variant="body1" sx={{ letterSpacing: 1, color: item.label === "Total Price" ? "#111827" : "#6b7280",}}>{item.value}</Typography>
                </Box>
                {index !== 3 && <Divider sx={{ borderColor: "#d1d5db" }} />}
              </Box>
            ))}
          </Box>

          {/* Name and Contact Number Fields */}
          <TextField
            label="Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            InputLabelProps={{ style: { color: "#1f2937" } }} // Change label color
            InputProps={{
              style: { color: "#1f2937", borderColor: "#1f2937" }, // Text color
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#1f2937" }, // Default border color
                "&:hover fieldset": { borderColor: "#1f2937" }, // Hover border color
                "&.Mui-focused fieldset": { borderColor: "#1f2937" }, // Focus border color
              },
            }}
          />
          <TextField
            label="Contact Number"
            variant="outlined"
            fullWidth
            type="number"
            margin="normal"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            InputLabelProps={{ style: { color: "#1f2937" } }} // Change label color
            InputProps={{
              style: { color: "#1f2937", borderColor: "#1f2937" }, // Text color
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#1f2937" }, // Default border color
                "&:hover fieldset": { borderColor: "#1f2937" }, // Hover border color
                "&.Mui-focused fieldset": { borderColor: "#1f2937" }, // Focus border color
              },
            }}
          />

          <Box sx={{ textAlign: "center", mt: 2, color: "#1f2937" }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              ✅ Please confirm and double-check your details before proceeding.
            </Typography>
          </Box>

          {isError && (
            <Box sx={{ textAlign: "center", mt: 2, color: "red" }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                ❌ {dialogMessage}
              </Typography>
            </Box>
          )}

          {/* Button to trigger PayHere payment */}
          <Button
            variant="contained"
            color="primary"
            sx={{ mt:1.5, backgroundColor: '#1f2937', color: "white", '&:hover': { backgroundColor: 'gray' } }}
            fullWidth
            onClick={handleConfirm}
            disabled={!isConfirmEnabled}
          >
            Continue to Payment
          </Button>
        </CardContent>
      </Card>


      <Button
        variant="outlined"
        color="#1f2937"
        onClick={() => window.location.reload()}
        sx={{marginTop: "20px"}}
      >
        Back to Home
      </Button>

      {/* Dialog for displaying messages */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Booking Status</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: isError ? 'red' : 'inherit' }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.location.reload()} color="primary">
            Back to Home
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BookingConfirmation;
